import styled from '@emotion/styled';
import { useTheme } from '@emotion/react';
import { Badge, Button, Icon, Image, Text, useComponentTheme } from '@vmos2/design-system';
import { useConfig, useSettings } from '@vmos2/providers';
import { localTime, useIsMounted, useLocale } from '@vmos2/utils';
import { utcToZonedTime } from 'date-fns-tz';
import { darken } from 'polished';
import React, { useCallback, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { DiscountErrorReasons, UserPromotion as UserPromotionType } from '../../flow-types';
import { LoyaltyPromotionError } from '../..';

type NewExpiryBadgeProps = {
  children: string,
  dataTest: string,
  icon: string,
  mode: string,
  type: string,
};

const NewExpiryBadge = ({ children, dataTest, icon, mode, type }: NewExpiryBadgeProps) => (
  <Badge dataTest={dataTest} mode={mode} type={type}>
    <Text level={5} strong vAlign="center">
      {icon && <Icon mr={1} name={icon} size={12} />}
      {children}
    </Text>
  </Badge>
);

type UserPromotionProps = {
  errorReasons?: DiscountErrorReasons,
  isDisabled: boolean,
  isInactive: boolean,
  isInList?: boolean,
  isLoading: boolean,
  isMissingItems: boolean,
  isSelected: boolean,
  onChange?: (e: ChangeEvent, userPromotion: UserPromotionType) => void,
  onClick?: (userPromotion: UserPromotionType) => void,
  showDescription: boolean,
  userPromotion?: UserPromotionType,
};

export const UserPromotion = ({
  errorReasons,
  isDisabled = false,
  isInactive = false,
  isInList = false,
  isLoading = false,
  isMissingItems = false,
  isSelected = false,
  onChange,
  onClick,
  showDescription = true,
  userPromotion,
}: UserPromotionProps) => {
  const config = useConfig();
  const isMounted = useIsMounted();
  const settings = useSettings();
  const theme = useTheme();
  const componentTheme = useComponentTheme(theme => theme.components.UserReward);
  const { locale, dateFnsLocale } = useLocale(config, settings);
  const [didImageLoadFail, setDidImageLoadFail] = useState(false);
  const { t } = useTranslation('profile.rewards');
  const { timezone } = settings;
  const { t: tCommon } = useTranslation('common');
  const checkboxLabel = useRef();
  const [errorMessage, setErrorMessage] = useState();
  const hasError = isSelected && (!!errorReasons?.length || isMissingItems);
  const titleColor = isInactive ? theme.color.grey : componentTheme.titleColor;
  const textColor = isInactive ? theme.color.grey : componentTheme.color;

  const renderIcon = useCallback(() => {
    if (isLoading) {
      return <Icon fill={titleColor} name="Spinner" size={32} spin />;
    }

    if (isSelected) {
      return (
        <CheckBadge type={hasError ? 'danger' : 'brand'}>
          <Icon name={hasError ? 'Minus' : 'Check'} size={12} />
        </CheckBadge>
      );
    }

    if (onClick) {
      return <Icon fill={titleColor} name="ChevronRight" size={12} />;
    }

    if (onChange) {
      return (
        <PlusIcon borderColor={textColor}>
          <Icon fill={titleColor} name="Plus" size={12} />
        </PlusIcon>
      );
    }

    return null;
  }, [hasError, isLoading, isSelected, onChange, onClick, textColor, titleColor]);

  if (!userPromotion) {
    return null;
  }

  const { badgeInformation = {}, ...userPromotionProperties } = userPromotion;
  const { count, description, endsAt, image, name, uuid } = userPromotionProperties;
  const { daysUntilExpiry, showExpiryBadge, showNewBadge } = badgeInformation;
  const disabled = isDisabled || isLoading;
  const expiringDaysOptions = { ...{ context: daysUntilExpiry === 0 && 'zero' }, count: daysUntilExpiry };
  const expiryDateString = userPromotion.userReward ? userPromotion.userReward.usableUntil : endsAt;
  const hasCounter = count > 1;
  const hasImage = !!image && !didImageLoadFail;
  const icon = renderIcon();
  const promotionId = userPromotion.userReward?.uuid || uuid;

  return (
    <UserPromotionWrapper data-test="user-promotion" {...(isInList && { as: 'li' })}>
      <UserPromotionItem
        block
        componentTheme={componentTheme}
        hasError={hasError}
        hasIcon={!!icon}
        hasImage={hasImage}
        iconRight={icon}
        isInactive={isInactive}
        isInteractive={!!onChange || !!onClick}
        mode="custom"
        px={4}
        py={3}
        {...(disabled && { 'aria-disabled': true })}
        {...(onClick && !disabled && { onClick: () => onClick(userPromotionProperties) })}
        {...(!onClick && { as: 'div' })}
      >
        {!!hasImage && (
          <UserPromotionImageWrapper>
            {typeof image === 'string' ? (
              <UserPromotionImage alt="" onError={setDidImageLoadFail} src={image} />
            ) : (
              image
            )}
          </UserPromotionImageWrapper>
        )}

        <Text
          {...(onChange && { ariaHidden: true })}
          color={titleColor}
          css={theme => ({ flexWrap: 'wrap', gap: theme.margins.xs, pointerEvents: 'none' })}
          dataTest="user-promotion-text"
          groupContentForScreenReader
          strong
          textRef={checkboxLabel}
          vAlign="center"
        >
          {name}

          {(showNewBadge || showExpiryBadge) && (
            <NewExpiryBadges>
              {showNewBadge && (
                <NewExpiryBadge dataTest="user-promotion-badge-new" mode="UserReward:Badge:New" type="brand">
                  <span className="hide-text">, </span>
                  {t('rewardsList.new')}
                </NewExpiryBadge>
              )}

              {showExpiryBadge && (
                <NewExpiryBadge
                  dataTest="user-promotion-badge-expiry"
                  icon="Clock"
                  mode="UserReward:Badge:Expiry"
                  type="danger"
                >
                  <span className="hide-text">, {t('rewardsList.expiringDaysAriaLabel', expiringDaysOptions)}</span>
                  <span aria-hidden="true">{t('rewardsList.expiringDays', expiringDaysOptions)}</span>
                </NewExpiryBadge>
              )}
            </NewExpiryBadges>
          )}

          {!!hasCounter && (
            <UserPromotionCount
              dataTest="user-promotion-count"
              hasImage={hasImage}
              isInactive={isInactive}
              radius="circle"
              type="white"
            >
              <span className="hide-text">, {tCommon('quantity', { quantity: count })}</span>
              <span aria-hidden="true">{t('rewardsList.count', { count })}</span>
            </UserPromotionCount>
          )}

          {showDescription && !!description && (
            <Text
              color={textColor}
              css={theme => ({ fontWeight: theme.fontWeight.light, width: '100%' })}
              dataTest="user-promotion-description"
              level={4}
              mt={-1}
            >
              <span className="hide-text">, </span>
              {description}
            </Text>
          )}

          {!!expiryDateString && (
            <Text
              color={textColor}
              css={{ width: '100%' }}
              dataTest="user-promotion-valid-date"
              display="block"
              level={4}
            >
              <span className="hide-text">, </span>

              <Trans
                components={[<DateTime />]}
                i18nKey="validUntil"
                ns="discountItem"
                values={{
                  date: localTime.format(
                    utcToZonedTime(expiryDateString, timezone),
                    'DATE_TIME',
                    locale,
                    dateFnsLocale
                  ),
                }}
              />
            </Text>
          )}
        </Text>

        {!!onChange && (
          <Checkbox
            {...(disabled && { 'aria-disabled': true })}
            {...(hasError && { 'aria-invalid': true })}
            aria-label={`${checkboxLabel.current?.textContent || ''}${errorMessage ? `. ${errorMessage}` : ''}`}
            checked={isSelected}
            data-test={`user-promotion-checkbox-${promotionId}`}
            id={promotionId}
            onChange={e => {
              if (!disabled) {
                e.persist();
                onChange(e, userPromotionProperties);
              }
            }}
            type="checkbox"
          />
        )}
      </UserPromotionItem>

      {isSelected && (
        <LoyaltyPromotionError
          errorReasons={errorReasons}
          errorRef={errorElement => {
            // Set this with a timeout so that it's not read out twice when the checkbox is checked (this issue only
            // happens with some screen readers) — once from the announcement in the `LoyaltyPromotionError` component,
            // and a second time because the text has changed at the time of setting it to checked.
            window.setTimeout(() => {
              if (isMounted.current && errorElement?.textContent !== errorMessage) {
                setErrorMessage(errorElement?.textContent);
              }
            }, 0);
          }}
          isMissingItems={isMissingItems}
        />
      )}
    </UserPromotionWrapper>
  );
};

const CheckBadge = styled(Badge)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
`;

const Checkbox = styled.input`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: inherit;
`;

const DateTime = styled.span`
  display: inline-block;
`;

const NewExpiryBadges = styled.div(
  ({ theme }) => `
    display: flex;
    flex-wrap: wrap;
    gap: ${theme.margins.xxs} ${theme.margins.xs};
    order: -1;
    width: 100%;
  `
);

const PlusIcon = styled.div(
  ({ borderColor, theme }) => `
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border: ${theme.stroke.light};
    border-color: ${borderColor};
    border-radius: ${theme.borderRadius.circle};
  `
);

const UserPromotionCount = styled(Badge)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  box-shadow: 0 2px 4px rgb(109 114 120 / 25%);
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme, isInactive }) => (isInactive ? theme.color.grey : theme.color.black)};

  ${({ hasImage }) =>
    hasImage &&
    `
      position: absolute;
      top: 50%;
      left: -34px;
      transform: translateY(calc(-100% + -8px));
    `}
`;

const UserPromotionImage = styled(Image)`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

// Use `:not(:empty)` in case the theming for the `LoyaltyLogo` has `visible: false`.
const UserPromotionImageWrapper = styled.div`
  &:not(:empty) {
    flex: none;
    width: 48px;
    height: 48px;
  }
`;

const UserPromotionItem = styled(Button)(
  ({
    componentTheme: { backgroundColor, border, borderRadius, boxShadow },
    hasError,
    hasIcon,
    hasImage,
    isInactive,
    isInteractive,
    theme,
  }) => `
    display: grid;
    column-gap: ${theme.margins.md};
    grid-template-columns: ${hasImage ? 'auto' : ''} 1fr ${hasIcon ? 'auto' : ''};
    border: ${hasError ? theme.stroke.error : border || theme.stroke.brand};
    border-radius: ${borderRadius};
    box-shadow: ${boxShadow || theme.boxShadow.small};
    font-size: ${theme.fontSize.lg};
    line-height: ${theme.lineHeight};
    text-align: left;
    background-color: ${backgroundColor};
    transition: none;

    ${
      isInteractive
        ? `
            &:hover:enabled:not([aria-disabled="true"]) {
              background-color: ${darken(0.05, backgroundColor)};
            }

            &:active:enabled:not([aria-disabled="true"]) {
              background-color: ${darken(0.08, backgroundColor)};
            }
          `
        : 'cursor: default;'
    }

    ${
      isInactive &&
      `
        opacity: 0.4;
        ${hasError ? '' : `border-color: ${theme.color.grey};`}

        :hover {
          background-color: ${backgroundColor};
        }
      `
    }
  `
);

const UserPromotionWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
`;
